import { createApp } from "vue";
import { createPinia } from "pinia";
import i18n from "./i18n";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import Hotjar from "vue-hotjar";
import Toast from "vue-toastification";
import { optionsToasts } from "@follaw-fr/components/reusable/minerve/toastMinerve";
import { Chart, registerables } from "chart.js";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isBetween from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";
import * as Sentry from "@sentry/vue";
import { inject } from "@vercel/analytics";
import focusVisible from "@follaw-fr/directives/focusVisible";

import "@/assets/sass/override_toast.scss";
import "shepherd.js/dist/css/shepherd.css";

import App from "@/App.vue";

import "./styles/main.css";
import "./assets/sass/style.scss";

import router from "./follaw-fr/router";
import SVFontAwesome from "./plugins/fontawesome";

// Auto-inject dev token before Pinia/stores are created
if (import.meta.env.VITE_DEV_TOKEN) {
  const devToken = import.meta.env.VITE_DEV_TOKEN;
  localStorage.setItem(
    "follaw_access_token",
    devToken.startsWith("Bearer ") ? devToken : "Bearer " + devToken
  );
}

const IS_PRODUCTION = import.meta.env.VITE_ENV === "production";

// DayJS config
dayjs.extend(utc);
dayjs.locale("fr");
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Paris");
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);

dayjs.extend(relativeTime, {
  rounding: Math.floor, // default is Math.round
});

// Toast config
const toastOptions = optionsToasts;

Chart.register(...registerables);

const app = createApp(App);
app
  .use(SVFontAwesome)
  .use(createPinia())
  .use(Hotjar, {
    id: import.meta.env.VITE_HOTJAR_ID,
    isProduction: IS_PRODUCTION,
  })
  .use(i18n)
  .use(router)
  .use(Toast, toastOptions);

Sentry.init({
  app,
  environment: import.meta.env.MODE || "development",
  dsn: import.meta.env.VITE_SENTRY_URL,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  // We recommend adjusting this value in production
  tracesSampleRate: 0.25,
  // Control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [import.meta.env.VITE_VUE_APP_API_URL],
  // Session Replay
  replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%.
  replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
});

// Vercel Analytics
inject();

// Singleton config object, used to store global variables
window._fsv_config = {
  ACCESS_TOKEN_KEY: "follaw_access_token",
};

app.directive("focus", focusVisible);

app.mount("#app");
